import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../layouts/DashboardLayout';
import {
  getWaQuickReplies, createWaQuickReply, updateWaQuickReply, deleteWaQuickReply
} from '../../../services/api';
import { toast } from 'sonner';
import {
  Zap, Plus, Edit2, Trash2, RefreshCw, X, Users, Settings,
  MessageCircle, Inbox, Bot, FileText, Bell
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Cuentas', path: '/superadmin/whatsapp', icon: MessageCircle },
  { label: 'Inbox', path: '/superadmin/whatsapp/inbox', icon: Inbox },
  { label: 'Auto-respuestas', path: '/superadmin/whatsapp/auto-replies', icon: Bot },
  { label: 'Plantillas', path: '/superadmin/whatsapp/templates', icon: FileText },
  { label: 'Resp. Rapidas', path: '/superadmin/whatsapp/quick-replies', icon: Zap },
  { label: 'Contactos', path: '/superadmin/whatsapp/contacts', icon: Users },
  { label: 'Eventos', path: '/superadmin/whatsapp/event-settings', icon: Settings },
  { label: 'Notificaciones', path: '/superadmin/whatsapp/notifications', icon: Bell },
];

const EMPTY_FORM = {
  title: '', body: '', category: 'general', shortcut: '', sort_order: 0,
};

const WhatsAppQuickReplies = () => {
  const navigate = useNavigate();
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadReplies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWaQuickReplies();
      setReplies(res.data?.quick_replies || []);
    } catch { toast.error('Error cargando respuestas rapidas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadReplies(); }, [loadReplies]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (qr) => {
    setEditing(qr);
    setForm({
      title: qr.title || '',
      body: qr.body || '',
      category: qr.category || 'general',
      shortcut: qr.shortcut || '',
      sort_order: qr.sort_order ?? 0,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.body) {
      toast.error('Completa titulo y cuerpo');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, shortcut: form.shortcut || null };
      if (editing) {
        await updateWaQuickReply(editing.id, payload);
        toast.success('Actualizada');
      } else {
        await createWaQuickReply(payload);
        toast.success('Creada');
      }
      setShowModal(false);
      loadReplies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando');
    } finally { setSaving(false); }
  };

  const handleDelete = async (qr) => {
    if (!window.confirm(`Eliminar "${qr.title}"?`)) return;
    try {
      await deleteWaQuickReply(qr.id);
      toast.success('Eliminada');
      loadReplies();
    } catch { toast.error('Error eliminando'); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Sub-nav */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b border-slate-200">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const active = item.path === '/superadmin/whatsapp/quick-replies';
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  active ? 'bg-green-100 text-green-700 font-medium' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}>
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Respuestas Rapidas</h1>
              <p className="text-sm text-slate-500">Mensajes predefinidos para usar en el inbox</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={openCreate}
              className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700">
              <Plus className="w-4 h-4" /> Nueva
            </button>
            <button onClick={loadReplies}
              className="p-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Cargando...</div>
          ) : replies.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Zap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No hay respuestas rapidas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium text-slate-600">Titulo</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Cuerpo</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Categoria</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Atajo</th>
                    <th className="px-4 py-3 font-medium text-slate-600 text-center">Orden</th>
                    <th className="px-4 py-3 font-medium text-slate-600 text-center">Usos</th>
                    <th className="px-4 py-3 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {replies.map((qr) => (
                    <tr key={qr.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{qr.title}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs max-w-[300px] truncate">{qr.body}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{qr.category}</span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{qr.shortcut || '-'}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{qr.sort_order}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{qr.usage_count || 0}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(qr)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-blue-600">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(qr)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">
                {editing ? 'Editar Respuesta Rapida' : 'Nueva Respuesta Rapida'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Titulo *</label>
                <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Saludo" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cuerpo del mensaje *</label>
                <textarea value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                  rows={4}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Hola! Gracias por contactarnos..." />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Categoria</label>
                  <input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="general" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Atajo</label>
                  <input value={form.shortcut} onChange={e => setForm(p => ({ ...p, shortcut: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                    placeholder="/saludo" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Orden</label>
                  <input type="number" value={form.sort_order}
                    onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-slate-200">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200">Cancelar</button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50">
                {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default WhatsAppQuickReplies;
